/**
 * Single source of truth for the tenant's brand identity.
 *
 * IMPORTANT: This file is hand-authored and intentionally does NOT read from
 * the generated theme/config layer (tenant.config.ts, theme/tokens.*,
 * localeProfile.ts). Those files are regenerated by the platform tooling and
 * periodically revert to their seed values, which previously caused brand
 * details to flip back unexpectedly.
 *
 * To re-brand the entire prototype, change the values in THIS file only.
 * Every feature/data file imports `BRAND` from here, so there is no brand
 * string hardcoded anywhere else in the app.
 *
 * Note: CSS-level color tokens (used by Tailwind arbitrary values like
 * `bg-[var(--brand-primary)]`) live in `index.css`. Keep the `colors` values
 * below in sync with the `--brand-*` custom properties defined there — both
 * point at the same canonical CIMB Red palette.
 */

export interface BrandColors {
  /** Primary brand color. */
  primary: string
  /** Hover/active shade of the primary color. */
  primaryHover: string
  /** Darker shade used for active text on tinted backgrounds. */
  primaryDark: string
  /** 10% alpha tint of the primary color. */
  primaryLight: string
  /** 3% alpha tint of the primary color. */
  primarySubtle: string
  /** 20% alpha tint used for focus rings. */
  primaryRing: string
  /** 30% alpha tint used for active focus rings. */
  primaryRingActive: string
}

export interface BrandAddress {
  line1: string
  city: string
  postalCode: string
  country: string
  /** Single-line representation, e.g. for legal/dispute correspondence. */
  oneLine: string
}

export interface Brand {
  /** Display name of the brand / settlement bank, e.g. "CIMB". */
  name: string
  /** Short code used in bank references and account labels, e.g. "CIMB". */
  shortCode: string
  /** Primary logo URL (used across login, sidebar, invoices, etc.). */
  logoUrl: string
  /** Splash/hero image shown on the login screen. */
  heroImageUrl: string
  /** Brand color palette (mirror of the `--brand-*` vars in index.css). */
  colors: BrandColors
  bank: {
    /** Bank routing / SWIFT-style code shown on payout account details. */
    swift: string
  }
  address: BrandAddress
  /** Primary web/email domain, e.g. "cimb.com.my". */
  domain: string
  /** Host used for sample webhook endpoints, e.g. "uat.cimb.com.my". */
  webhookHost: string
  /** Display name of the brand's card product, e.g. "CIMB Card". */
  cardName: string
  /** Label for the brand's balance product, e.g. "CIMB Balance". */
  balanceLabel: string
  /** Internal note shown against payouts, e.g. "CIMB PAYOUT". */
  payoutNote: string
}

const NAME = 'Maybank'
const DOMAIN = 'maybank2u.com.my'

export const BRAND: Brand = {
  name: NAME,
  shortCode: 'MBB',
  logoUrl:
    'https://cdn.magicpatterns.com/uploads/tTsEDdFjXBHCxDyTZkCLnG/Maybank_logo.svg',
  heroImageUrl:
    'https://cdn.magicpatterns.com/uploads/fALiiuPfjAHHQf4jNQTttw/maybank-new-head-office-relocation-menara-merdeka-118.webp',
  colors: {
    primary: '#FFCC00',
    primaryHover: '#E6B800',
    primaryDark: '#CCA300',
    primaryLight: '#FFCC001A',
    primarySubtle: '#FFCC0008',
    primaryRing: '#FFCC0033',
    primaryRingActive: '#FFCC004D',
  },
  bank: {
    swift: 'MBBEMYKL',
  },
  address: {
    line1: 'Menara Maybank, 100 Jalan Tun Perak',
    city: 'Kuala Lumpur',
    postalCode: '50050',
    country: 'Malaysia',
    oneLine:
      'Menara Maybank, 100 Jalan Tun Perak, 50050 Kuala Lumpur, Malaysia',
  },
  domain: DOMAIN,
  webhookHost: `uat.${DOMAIN}`,
  cardName: `${NAME} Card`,
  balanceLabel: `${NAME} Balance`,
  payoutNote: `${NAME} PAYOUT`,
}

/** Builds an email address on the brand domain, e.g. "support@cimb.com.my". */
export function brandEmail(local: string): string {
  return `${local}@${BRAND.domain}`
}
